import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Map} from 'immutable';
import {makeStyles} from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import {lightTheme} from '@components/@theme/lightTheme';
import {CircledCheck} from './CircledCheck';
import {
  exampleKpiValue,
  KpiRequest,
  KpiTarget,
  KpiTargetId,
  validateKpiValue,
} from '@store/@types/kpi';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import NextPreviousSelector from '@components/next-previous-selector';
import {List} from 'immutable';
import User, {UserId} from 'records/User';
import KpiObservationActions from '@store/kpi/KpiObservationActions';
import KpiGoalActions from '@store/kpi/KpiTargetActions';
import {Moment} from 'moment';
import {SortDirection} from 'utils/const';
import {parseCurrency} from 'utils/parseCurrency';

interface KpiProgressBannerProps {
  kpiRequestsByUser: Map<UserId, List<KpiRequest>>;
  fetchObservations: typeof KpiObservationActions.search;
  fetchRequests: typeof KpiObservationActions.getReminders;
  fetchKpis: typeof KpiGoalActions.fetchGoalKpiTree;
  submit: typeof KpiObservationActions.create;
  targetUser: User;
  authUser: User;
  kpisById: Map<KpiTargetId, KpiTarget>;
  startDate: Moment;
  endDate: Moment;
  dashOnly?: boolean;
}

export const KpiProgressBanner: React.FC<KpiProgressBannerProps> = ({
  dashOnly,
  kpiRequestsByUser,
  fetchObservations,
  fetchRequests,
  fetchKpis,
  kpisById,
  submit,
  targetUser,
  authUser,
  startDate,
  endDate,
}) => {
  const kpiRequests = useMemo(
    () =>
      kpiRequestsByUser.get(targetUser.userId, List()).filter(k => {
        return dashOnly ? k.askViaDashboard : true;
      }),
    [kpiRequestsByUser, targetUser, dashOnly]
  );
  useEffect(() => {
    if (targetUser.id === authUser.id) {
      fetchRequests.defer(targetUser.id);
      fetchKpis.defer();
    }
  }, [fetchRequests, fetchKpis, targetUser.id, authUser.id]);

  const [submitting, setSubmitting] = useState(false);
  const [value, setValue] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [index, setIndex] = useState(0);
  const onNext = useCallback(() => {
    if (index + 1 < kpiRequests.size) {
      setIndex(index + 1);
    }
  }, [index, setIndex, kpiRequests]);
  const onPrev = useCallback(() => {
    if (index !== 0) {
      setIndex(index - 1);
    }
  }, [index, setIndex]);
  const onChange = useCallback(
    e => {
      setValue(e.target.value);
    },
    [setValue]
  );

  const kpiRequest = kpiRequests.get(index);
  const kpi = kpisById.get(kpiRequest?.id);

  const onSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await submit({
        kpiId: kpiRequest.id,
        userId: targetUser.id,
        value: parseCurrency(value),
      });
      fetchObservations.defer({
        startDate,
        endDate,
        userId: targetUser.id,
        search: '',
        page: 1,
        sortDirection: SortDirection.DESC,
      });
      const [requests] = await fetchRequests(targetUser.id);
      if (requests.isEmpty()) {
        setShowSuccess(true);
      }
      setValue('');
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const {template, actions, emptyState, pager, wrapper, content, textField} = useStyles(lightTheme);
  // This should only be shown if we submitted the final KPI on this session.
  // after refresh it shouldnt show
  if (showSuccess) {
    return (
      <Card className={wrapper} variant="outlined">
        <div className={emptyState}>
          <CircledCheck />
          Thanks for submitting your achievements!
        </div>
      </Card>
    );
  }

  if (!kpiRequest) {
    return null;
  }

  const error = kpi && validateKpiValue(kpi.numericType, value);

  return (
    <Card className={wrapper} variant="outlined">
      {kpiRequests.size > 1 && (
        <NextPreviousSelector
          className={pager}
          isNextDisabled={kpiRequests.size === index + 1}
          isPrevDisabled={index === 0}
          onPrevious={onPrev}
          onNext={onNext}
        />
      )}
      <div className={content}>
        <div className={template}>{kpiRequest.template}</div>
        <div className={actions}>
          <TextField
            error={!!error}
            helperText={error && `Entry may only include numbers and ${error?.join(' ')}`}
            placeholder={kpi && exampleKpiValue(kpi.numericType)}
            onChange={onChange}
            className={textField}
            variant="outlined"
            value={value}
          />
          <Button
            disabled={!value || !!error || submitting}
            onClick={onSubmit}
            color="primary"
            variant="contained">
            Submit
          </Button>
        </div>
      </div>
    </Card>
  );
};

const useStyles = makeStyles(theme => ({
  wrapper: {
    padding: '16px',
    position: 'relative',
    minHeight: '100px',
    display: 'grid',
  },
  actions: {
    marginTop: '16px',
  },
  content: {
    margin: '0 200px 0 100px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  },
  emptyState: {
    margin: '0 100px',
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    '& > svg': {
      marginRight: '10px',
    },
  },
  textField: {
    width: '100px',
    marginRight: '10px',
    '& > .MuiFormHelperText-contained': {
      whiteSpace: 'nowrap',
    },
  },
  pager: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '160px',
  },
  template: {
    whiteSpace: 'pre-wrap',
  },
}));
